/**
 * Custom domains.
 *
 * WHY THIS IS A PLATFORM SCREEN AND NOT A TENANT ONE. Pointing a hostname at an
 * organisation is only half the job - the other half is DNS and a Firebase
 * Hosting custom domain, neither of which a tenant can do from inside the
 * product. A form that lets a customer type an address and then silently does
 * nothing until somebody else creates a CNAME is a form that generates support
 * tickets, so it lives here, next to the person who does the other half.
 *
 * THE THREE STEPS ARE SPELLED OUT ON THE PAGE for the same reason. Mapping the
 * host here is step three; doing it first produces a record that resolves to
 * nothing, and the failure appears at the customer's browser rather than here.
 */

import { useCallback, useEffect, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from './firebase';

interface HostRow {
  host: string;
  orgId: string | null;
  kind: 'tenant' | 'admin';
  title: string;
  hasLogo: boolean;
  updatedAt: number | null;
}

interface OrgOption { orgId: string; name: string }

export function HostsView({ orgs }: { orgs: OrgOption[] }) {
  const [rows, setRows] = useState<HostRow[] | null>(null);
  const [host, setHost] = useState('');
  const [orgId, setOrgId] = useState('');
  const [kind, setKind] = useState<'tenant' | 'admin'>('tenant');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);

  const load = useCallback(async () => {
    try {
      const r = await httpsCallable(functions, 'adminListHosts')({});
      setRows((r.data as { hosts: HostRow[] }).hosts ?? []);
    } catch (err) {
      setMsg({ ok: false, text: err instanceof Error ? err.message : 'Could not load domains.' });
      setRows([]);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  async function run(fn: () => Promise<string>) {
    setBusy(true); setMsg(null);
    try {
      setMsg({ ok: true, text: await fn() });
      await load();
    } catch (err) {
      setMsg({ ok: false, text: err instanceof Error ? err.message : 'That did not work.' });
    } finally { setBusy(false); }
  }

  return (
    <section className="panel">
      <div className="panel-title">Custom domains</div>
      <p className="panel-note">
        A tenant on its own address sees its own name and logo on the sign-in page, before
        anybody has signed in. Three steps, in this order:
      </p>
      <ol className="panel-note" style={{ paddingLeft: 18 }}>
        <li>Point the DNS at Firebase Hosting and add the domain to the right hosting site.</li>
        <li>Add it to <strong>Authorised sign-in domains</strong> above, or nobody can sign in there.</li>
        <li>Map it to an organisation here.</li>
      </ol>

      {msg && <div className={'banner ' + (msg.ok ? 'banner-ok' : 'banner-error')}>{msg.text}</div>}

      {rows === null ? <p className="empty">Loading.</p> : rows.length === 0 ? (
        <p className="empty">No custom domains yet.</p>
      ) : (
        <table className="mini-table">
          <thead>
            <tr><th>Hostname</th><th>Serves</th><th>Shows</th><th>Branding</th><th></th></tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.host}>
                <td className="mono">{r.host}</td>
                <td>{r.kind === 'admin' ? 'Platform console' : (orgs.find((o) => o.orgId === r.orgId)?.name ?? r.orgId ?? '-')}</td>
                <td>{r.title}</td>
                <td>
                  {/* Named rather than ticked: "no logo" and "we did not check"
                      look identical as an empty cell, and the difference is
                      whether somebody still has work to do. */}
                  {r.hasLogo ? 'Logo' : 'No logo'}
                </td>
                <td>
                  <button
                    className="btn-link danger" disabled={busy}
                    onClick={() => {
                      if (!confirm(`Stop serving branding for ${r.host}?`)) return;
                      void run(async () => {
                        await httpsCallable(functions, 'adminRemoveHost')({ host: r.host });
                        return `${r.host} unmapped. It will show the default branding.`;
                      });
                    }}
                  >
                    Remove
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <div className="row-form">
        <label className="field" style={{ flex: 2 }}>
          <span className="field-label">Hostname</span>
          <input
            className="field-input" placeholder="backups.example.com"
            value={host} onChange={(e) => setHost(e.target.value)}
          />
        </label>
        <label className="field">
          <span className="field-label">Serves</span>
          <select
            className="field-input" value={kind}
            onChange={(e) => setKind(e.target.value === 'admin' ? 'admin' : 'tenant')}
          >
            <option value="tenant">A tenant</option>
            <option value="admin">The platform console</option>
          </select>
        </label>
        {kind === 'tenant' && (
          <label className="field" style={{ flex: 2 }}>
            <span className="field-label">Organisation</span>
            <select className="field-input" value={orgId} onChange={(e) => setOrgId(e.target.value)}>
              <option value="">Choose one</option>
              {orgs.map((o) => <option key={o.orgId} value={o.orgId}>{o.name}</option>)}
            </select>
          </label>
        )}
        <button
          className="btn btn-primary"
          disabled={busy || !host.trim() || (kind === 'tenant' && !orgId)}
          onClick={() => void run(async () => {
            await httpsCallable(functions, 'adminSetHost')({ host, orgId, kind });
            const h = host.trim();
            setHost('');
            return `${h} mapped.`;
          })}
        >
          {busy ? 'Saving.' : 'Map'}
        </button>
      </div>
    </section>
  );
}
